
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { MicroserviceValidation } from '@/modules/GatewayModule/validations/Microservice.validation'

// classes
import { GatewayMicroserviceT, GatewayScopeT } from '@/core/types/gateway.types'
import { DataState } from '@/core/classes/DataState.class'
import { gateway } from '@/core/realisations/gateway.realisation'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
        checkbox: Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<GatewayMicroserviceT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            name: null as string | null,
            host: null as string | null,
            scopes: [] as number[],
            isActive: false,
        })

        const state_form$ = useVuelidate(MicroserviceValidation, state)

        const data_state = reactive({
            scopes: new DataState<GatewayScopeT>({}),
        })

        const loadScopes = async (page: number) => {
            data_state.scopes.pagination.current_page = page

            try {
                data_state.scopes.is_loading = true

                const response = await gateway.getScopes(page)
                data_state.scopes.list = response.data
                data_state.scopes.pagination.total_pages = response.meta.last_page

                data_state.scopes.is_failed = false
                data_state.scopes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.scopes.failure.catchError(error)

                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_failed = true
                }
            } finally {
                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_loading = false
                }
            }
        }

        const loadMoreScopes = async (page: number) => {
            if (page > data_state.scopes.pagination.total_pages) return
            data_state.scopes.pagination.current_page = page

            try {
                data_state.scopes.is_loading = true

                const response = await gateway.getScopes(page)
                data_state.scopes.list = data_state.scopes.list.concat(response.data)
                data_state.scopes.pagination.total_pages = response.meta.last_page

                data_state.scopes.is_failed = false
                data_state.scopes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.scopes.failure.catchError(error)

                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_failed = true
                }
            } finally {
                if (!data_state.scopes.failure.error_state.is_cancel) {
                    data_state.scopes.is_loading = false
                }
            }
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление микросервиса'
            if (props.isUpdate) return 'Редактирование микросервиса'
            if (props.isDelete) return 'Удаление микросервиса'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const payload = {
                name: state.name,
                host: state.host,
                isActive: state.isActive,
                scopes: state.scopes,
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', toRaw(state))
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        onBeforeMount(() => {
            loadScopes(1)
            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.name = props.initialValue.name
            state.host = props.initialValue.host
            state.isActive = Boolean(props.initialValue.isActive)
            state.scopes = props.initialValue.scopes.map((scope) => scope.id)
        })

        return {
            state,
            state_form$,
            data_state,
            loadScopes,
            loadMoreScopes,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
        }
    },
})
